// Data-access layer for the Teatro front.
//  - Reads (GET)  -> GraphQL  (Apollo client from ./graphql)
//  - Writes (POST) -> REST API (axios)
//
// Components consume the same object shape they used from teatroData.js:
// { id, title, image, description, videoUrl, genre, synopsis, facts, ... }.

import gql from "graphql-tag";
import axios from "axios";
import apollo from "../graphql";

// Minutos -> "HH:MM" (ej: 70 -> "01:10").
function formatDuration(minutes) {
  const total = Number(minutes) || 0;
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

// --- Mappers backend -> front shape ------------------------------------------

export function mapPlayCard(node) {
  // Minimal data for the grid card.
  return {
    id: node.id,
    title: node.title,
    image: node.image,
    description: node.description,
    videoUrl: node.videoUrl,
  };
}

export function mapPlayDetail(node) {
  // Full data for the detail and reservation views.
  return {
    id: node.id,
    title: node.title,
    image: node.image,
    description: node.description,
    videoUrl: node.videoUrl,
    genre: node.genre ? node.genre.name : null,
    synopsis: node.synopsis,
    startDate: node.startDate,
    endDate: node.endDate,
    blockedDates: node.blockedDates || [],
    price: node.priceDisplay,
    priceRegion: node.priceRegion,
    priceRegionDisplay: node.priceRegionDisplay,
    facts: [
      { label: "COMPAÑÍA", value: node.company ? node.company.name : "" },
      { label: "DURACIÓN", value: formatDuration(node.durationMinutes) },
      { label: "PÚBLICO", value: node.audience },
      { label: "ESCENARIO", value: node.stage },
      { label: "GÉNERO", value: node.genre ? node.genre.name : "" },
      { label: "DIRECTORES", value: node.directors },
      { label: "ACTORES", value: node.actors },
    ],
  };
}

// --- Queries (GraphQL) -------------------------------------------------------

const GENRES_QUERY = gql`
  query {
    genres {
      id
      name
      slug
      tag
      image
      plays {
        id
        title
        image
        description
        videoUrl
      }
    }
  }
`;

const PLAY_QUERY = gql`
  query ($id: ID!) {
    play(id: $id) {
      id
      title
      image
      description
      synopsis
      videoUrl
      company {
        name
      }
      durationMinutes
      audience
      stage
      directors
      actors
      priceDisplay
      priceRegion
      priceRegionDisplay
      startDate
      endDate
      blockedDates
      genre {
        name
      }
    }
  }
`;

// Returns { genres: [...], genreShows: { [name]: [cards] } } for the landing.
export async function fetchLanding() {
  const { data } = await apollo.query({
    query: GENRES_QUERY,
    fetchPolicy: "no-cache",
  });
  const genres = [];
  const genreShows = {};
  (data.genres || []).forEach((g) => {
    genres.push({ name: g.name, tag: g.tag, image: g.image, slug: g.slug });
    genreShows[g.name] = (g.plays || []).map(mapPlayCard);
  });
  return { genres, genreShows };
}

// Returns a play (detail shape) by id, or null if it doesn't exist.
export async function fetchPlay(id) {
  const { data } = await apollo.query({
    query: PLAY_QUERY,
    variables: { id: String(id) },
    fetchPolicy: "no-cache",
  });
  return data.play ? mapPlayDetail(data.play) : null;
}

// Returns the authenticated user's profile, or null if not logged in.
export async function fetchMe() {
  const ME_QUERY = gql`
    query {
      me {
        id
        email
        firstName
        lastName
        fullName
        phone
        identificator
        role
        stageSurface
        budget
        preferredSeason
        ageRange
        interestGenres {
          id
          name
        }
        company {
          id
          name
          logo
          clientType
        }
      }
    }
  `;
  const { data } = await apollo.query({
    query: ME_QUERY,
    fetchPolicy: "no-cache",
  });
  return data.me || null;
}

// Returns up to 4 plays matching the search query (card shape).
export async function searchPlays(query) {
  const SEARCH_QUERY = gql`
    query ($query: String!, $limit: Int) {
      searchPlays(query: $query, limit: $limit) {
        id
        title
        image
        description
        videoUrl
      }
    }
  `;
  const { data } = await apollo.query({
    query: SEARCH_QUERY,
    variables: { query, limit: 4 },
    fetchPolicy: "no-cache",
  });
  return (data.searchPlays || []).map(mapPlayCard);
}

// --- Preguntas frecuentes (GraphQL) -----------------------------------------

const FAQS_QUERY = gql`
  query {
    faqs {
      id
      question
      answer
      category
    }
  }
`;

// Devuelve las preguntas frecuentes activas, ya ordenadas por el backend.
export async function fetchFaqs() {
  const { data } = await apollo.query({
    query: FAQS_QUERY,
    fetchPolicy: "no-cache",
  });
  return data.faqs || [];
}

// --- Reservation queries (GraphQL) -----------------------------------------

const RESERVATION_FRAGMENT = gql`
  fragment ReservationFields on ReservationType {
    id
    status
    statusDisplay
    displayDate
    requestedDate
    confirmedDate
    totalAmount
    depositAmount
    balanceAmount
    name
    rut
    phone
    email
    notes
    clientComment
    depositPaidAt
    balancePaidAt
    contractFileUrl
    contractFile
    contractSignedAt
    contractSentAt
    companyContractSignedAt
    playAmount
    reservationInvoiceUrl
    reservationInvoice
    depositProofUrl
    depositProof
    balanceInvoiceUrl
    balanceInvoice
    balanceProofUrl
    balanceProof
    companyInvoiceUrl
    companyInvoice
    companyInvoiceAt
    companyPaymentProofUrl
    companyPaymentProof
    companyPaidAt
    companyAdvanceProofUrl
    companyAdvancePaidAt
    companyAdvanceInvoiceUrl
    companyAdvanceInvoiceAt
    companyAdvanceAmount
    companyBalanceAmount
    cancelledAt
    cancelReason
    cancelledFromStatus
    cancelledByName
    cancelledByRole
    created
    updated
    play {
      id
      title
      company {
        name
      }
      priceDisplay
      image
    }
    client {
      id
      email
      fullName
      company {
        name
        city
      }
    }
  }
`;

const MY_RESERVATIONS_QUERY = gql`
  query MyReservations {
    myReservations {
      ...ReservationFields
    }
  }
  ${RESERVATION_FRAGMENT}
`;

const RESERVATION_DETAIL_QUERY = gql`
  query ReservationDetail($id: ID!) {
    reservation(id: $id) {
      ...ReservationFields
    }
  }
  ${RESERVATION_FRAGMENT}
`;

export async function fetchMyReservations() {
  const { data } = await apollo.query({
    query: MY_RESERVATIONS_QUERY,
    fetchPolicy: "no-cache",
  });
  return data.myReservations || [];
}

export async function fetchReservation(id) {
  const { data } = await apollo.query({
    query: RESERVATION_DETAIL_QUERY,
    variables: { id: String(id) },
    fetchPolicy: "no-cache",
  });
  return data.reservation;
}

// Reservations (functions) for the "Historial de negocios" view. Scoped by the
// backend to the company's plays (managers) or all plays (admin). Includes the
// play season dates so the front can group plays by exhibition year.
const HISTORY_QUERY = gql`
  query BusinessHistory {
    myReservations {
      id
      status
      statusDisplay
      displayDate
      name
      client {
        company {
          city
        }
      }
      play {
        id
        title
        startDate
        endDate
      }
    }
  }
`;

export async function fetchBusinessHistory() {
  const { data } = await apollo.query({
    query: HISTORY_QUERY,
    fetchPolicy: "no-cache",
  });
  return data.myReservations || [];
}

// --- Write mutations (REST) --------------------------------------------------

// Creates a reservation. payload: { play, name, rut, phone, email, dates, client_comment }
export async function createReservation(payload) {
  const { data } = await axios.post("/api/reservations/", payload, {
    headers: { "Content-Type": "application/json" },
  });
  return data;
}

function postAction(url, body, isFile = false) {
  let requestBody;
  let headers = {};
  if (isFile) {
    requestBody = new FormData();
    // Vuetify 3 v-file-input entrega un array [File]; tomar el archivo real.
    const file = Array.isArray(body.file) ? body.file[0] : body.file;
    if (file) requestBody.append("file", file);
    if (body.notes) requestBody.append("notes", body.notes);
    if (body.confirmedDate) requestBody.append("confirmed_date", body.confirmedDate);
  } else {
    requestBody = body;
    headers["Content-Type"] = "application/json";
  }
  return axios.post(url, requestBody, { headers });
}

export async function confirmReservationDate(reservationId, confirmedDate) {
  const { data } = await postAction(`/api/my-reservations/${reservationId}/confirm_date/`, { confirmed_date: confirmedDate });
  return data;
}

// La compañía rechaza la fecha: la reserva vuelve a "disponibilidad de fecha".
export async function rejectReservationDate(reservationId, reason = "") {
  const { data } = await postAction(`/api/my-reservations/${reservationId}/reject_date/`, { reason });
  return data;
}

export async function uploadContract(reservationId, file, notes = "") {
  const { data } = await postAction(`/api/my-reservations/${reservationId}/upload_contract/`, { file, notes }, true);
  return data;
}

// Los contratos de la reserva y su detalle de precios:
// { cliente: {...}, cia: {...}, pricing: {...}, annex_html }. Cada parte recibe
// sólo el suyo; la administración ambos, con el borrador en `html`.
export async function fetchContracts(reservationId) {
  const { data } = await axios.get(`/api/my-reservations/${reservationId}/contratos/`);
  return data;
}

// Guarda el borrador de un contrato (kind: "cliente" | "cia"), sin enviarlo.
// Con `reset = true` vuelve al texto rellenado. Devuelve los contratos.
export async function saveContract(reservationId, kind, html, reset = false) {
  const body = reset ? { reset: true } : { html };
  const { data } = await postAction(`/api/my-reservations/${reservationId}/contrato/${kind}/`, body);
  return data;
}

// PDF de un contrato con el texto del editor y el anexo de precios, para
// revisarlo antes de enviarlo (no guarda ni envía). Devuelve un Blob.
export async function downloadContractPdf(reservationId, kind, html) {
  const { data } = await axios.post(
    `/api/my-reservations/${reservationId}/contrato/${kind}/pdf/`,
    { html },
    { headers: { "Content-Type": "application/json" }, responseType: "blob" }
  );
  return data;
}

// Precio de la obra y servicios extra: { play_amount, services: [{ name, amount }] }.
// Devuelve los contratos (con el anexo de precios actualizado).
export async function saveReservationPricing(reservationId, pricing) {
  const { data } = await postAction(`/api/my-reservations/${reservationId}/precios/`, pricing);
  return data;
}

// Genera los PDFs y envía cada contrato a su contraparte (cliente y compañía)
// con el enlace para aceptarlo. `drafts` = { cliente: html, cia: html }.
export async function sendContracts(reservationId, drafts) {
  const { data } = await postAction(`/api/my-reservations/${reservationId}/enviar-contratos/`, { html: drafts });
  return data;
}

// Vuelve a enviar por correo un contrato ya enviado (mismo PDF y enlace).
// Devuelve los contratos.
export async function resendContractEmail(reservationId, kind) {
  const { data } = await postAction(`/api/my-reservations/${reservationId}/reenviar-correo/`, { kind });
  return data;
}

// Envía el código de 6 dígitos para aceptar un contrato: el del cliente a su
// correo y el de la compañía al correo de la compañía.
export async function requestAcceptanceCode(reservationId, kind) {
  const { data } = await postAction(`/api/my-reservations/${reservationId}/codigo-aceptacion/`, { kind });
  return data;
}

// Acepta un contrato: { kind, accepted: true, version, code } (el código sólo
// lo pide el backend cuando acepta la contraparte; el admin acepta sin código).
export async function acceptContract(reservationId, payload) {
  const { data } = await postAction(`/api/my-reservations/${reservationId}/accept_contract/`, payload);
  return data;
}

export async function uploadReservationInvoice(reservationId, file, notes = "") {
  const { data } = await postAction(`/api/my-reservations/${reservationId}/upload_reservation_invoice/`, { file, notes }, true);
  return data;
}

export async function reuploadDepositProof(reservationId, file, notes = "") {
  const { data } = await postAction(`/api/my-reservations/${reservationId}/reupload_deposit_proof/`, { file, notes }, true);
  return data;
}

export async function generateReservationInvoice(reservationId, file, notes = "") {
  const { data } = await postAction(`/api/my-reservations/${reservationId}/generate_reservation_invoice/`, { file, notes }, true);
  return data;
}

export async function uploadDepositProof(reservationId, file, notes = "") {
  const { data } = await postAction(`/api/my-reservations/${reservationId}/upload_deposit_proof/`, { file, notes }, true);
  return data;
}

export async function generateBalanceInvoice(reservationId, file, notes = "") {
  const { data } = await postAction(`/api/my-reservations/${reservationId}/generate_balance_invoice/`, { file, notes }, true);
  return data;
}

export async function completeShow(reservationId) {
  const { data } = await postAction(`/api/my-reservations/${reservationId}/complete_show/`, {});
  return data;
}

export async function uploadBalanceProof(reservationId, file, notes = "") {
  const { data } = await postAction(`/api/my-reservations/${reservationId}/upload_balance_proof/`, { file, notes }, true);
  return data;
}

// --- Tramo administración <-> compañía de teatro (el cliente no lo ve) ------

// Comprobante del anticipo (10%) de la administración a la compañía (sólo admin).
export async function uploadCompanyAdvancePayment(reservationId, file, notes = "") {
  const { data } = await postAction(`/api/my-reservations/${reservationId}/upload_company_advance_payment/`, { file, notes }, true);
  return data;
}

// Factura de la compañía a la administración por el anticipo (10%).
export async function uploadCompanyAdvanceInvoice(reservationId, file, notes = "") {
  const { data } = await postAction(`/api/my-reservations/${reservationId}/upload_company_advance_invoice/`, { file, notes }, true);
  return data;
}

// Comprobante del saldo (80%) de la administración a la compañía (sólo admin).
export async function uploadCompanyPayment(reservationId, file, notes = "") {
  const { data } = await postAction(`/api/my-reservations/${reservationId}/upload_company_payment/`, { file, notes }, true);
  return data;
}

// Factura de la compañía a la administración por el saldo (80%).
export async function uploadCompanyInvoice(reservationId, file, notes = "") {
  const { data } = await postAction(`/api/my-reservations/${reservationId}/upload_company_invoice/`, { file, notes }, true);
  return data;
}

// Cancela la reserva con un motivo (obligatorio). Lo pueden hacer la
// administración, el cliente dueño y la compañía (estos dos sólo hasta la
// firma de contratos).
export async function cancelReservation(reservationId, reason) {
  const { data } = await postAction(`/api/my-reservations/${reservationId}/cancel/`, { reason });
  return data;
}

// --- Play / Blackout management (admin & company members) --------------------

export async function fetchPlaysAdmin() {
  const { data } = await axios.get("/api/plays/");
  return data;
}

export async function fetchPlayAdmin(id) {
  const { data } = await axios.get(`/api/plays/${id}/`);
  return data;
}

export async function createPlay(payload) {
  const { data } = await axios.post("/api/plays/", payload, {
    headers: { "Content-Type": "application/json" },
  });
  return data;
}

export async function updatePlay(id, payload) {
  const { data } = await axios.put(`/api/plays/${id}/`, payload, {
    headers: { "Content-Type": "application/json" },
  });
  return data;
}

export async function deletePlay(id) {
  await axios.delete(`/api/plays/${id}/`);
}

export async function uploadPlayImage(id, file) {
  const formData = new FormData();
  formData.append("file", file);
  const { data } = await axios.post(`/api/plays/${id}/upload_image/`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data; // { image: "<url>" }
}

export async function fetchBlackouts() {
  const { data } = await axios.get("/api/blackouts/");
  return data;
}

export async function createBlackout(payload) {
  const { data } = await axios.post("/api/blackouts/", payload, {
    headers: { "Content-Type": "application/json" },
  });
  return data;
}

export async function updateBlackout(id, payload) {
  const { data } = await axios.put(`/api/blackouts/${id}/`, payload, {
    headers: { "Content-Type": "application/json" },
  });
  return data;
}

export async function deleteBlackout(id) {
  await axios.delete(`/api/blackouts/${id}/`);
}

export async function fetchGenres() {
  const { data } = await axios.get("/api/genres/");
  return data;
}

// Guarda los intereses del usuario autenticado (perfil del cliente).
// payload: { interest_genres: [id...], stage_surface: "", budget: number|null }
export async function saveMyInterests(payload) {
  const { data } = await axios.post("/api/me/interests/", payload, {
    headers: { "Content-Type": "application/json" },
  });
  return data;
}

// Términos y condiciones en bloques (título, párrafos, listas y el PDF).
export async function fetchTerms() {
  const { data } = await axios.get("/api/terminos/");
  return data;
}

// Datos de la empresa que le faltan al cliente para poder reservar.
// -> { ready, missing: [...], message, company_id }
export async function fetchReservationRequirements() {
  const { data } = await axios.get("/api/me/requisitos-reserva/");
  return data;
}

export async function fetchTags() {
  const { data } = await axios.get("/api/tags/");
  return data;
}

export async function fetchCompanies() {
  const { data } = await axios.get("/api/companies/");
  return data;
}

export async function fetchCompany(id) {
  const { data } = await axios.get(`/api/companies/${id}/`);
  return data;
}

export async function createCompany(payload) {
  const { data } = await axios.post("/api/companies/", payload, {
    headers: { "Content-Type": "application/json" },
  });
  return data;
}

// --- Soportes (coordinador, sonido, …) ---------------------------------------
// Los contactos van anidados en el mismo objeto, en `contacts`.

export async function fetchSupports() {
  const { data } = await axios.get("/api/supports/");
  return data;
}

export async function fetchSupport(id) {
  const { data } = await axios.get(`/api/supports/${id}/`);
  return data;
}

export async function createSupport(payload) {
  const { data } = await axios.post("/api/supports/", payload, {
    headers: { "Content-Type": "application/json" },
  });
  return data;
}

export async function updateSupport(id, payload) {
  const { data } = await axios.put(`/api/supports/${id}/`, payload, {
    headers: { "Content-Type": "application/json" },
  });
  return data;
}

export async function deleteSupport(id) {
  await axios.delete(`/api/supports/${id}/`);
}

// --- Informes (Administración / Informes) ------------------------------------

// tipo: "hitos" | "contratos". Devuelve { tipo, count, truncated, results }.
export async function fetchReports(search = "", tipo = "hitos") {
  const { data } = await axios.get("/api/reports/", { params: { q: search, tipo } });
  return data;
}

// Sugerencias del buscador: { plays, companies, clients }.
export async function fetchReportOptions() {
  const { data } = await axios.get("/api/reports/opciones/");
  return data;
}

// --- Clientes (Administración / Clientes) ------------------------------------
// Un cliente es una cuenta de rol cliente + la empresa a la que pertenece.

// Los inactivos sólo vienen con `includeInactive`.
export async function fetchClients(search = "", clientType = "", companyType = "", includeInactive = false) {
  const params = {};
  if (search) params.q = search;
  if (clientType) params.client_type = clientType;
  if (companyType) params.company_type = companyType;
  if (includeInactive) params.inactivos = 1;
  const { data } = await axios.get("/api/clients/", { params });
  return data;
}

// Filtros del listado de clientes: { client_types: [{value, label}], company_types: [string] }.
export async function fetchClientFilterOptions() {
  const { data } = await axios.get("/api/clients/opciones/");
  return data;
}

// Rubros de cliente: [{ value, label }].
export async function fetchClientTypes() {
  const { data } = await axios.get("/api/client-types/");
  return data;
}

export async function fetchClient(id) {
  const { data } = await axios.get(`/api/clients/${id}/`);
  return data;
}

export async function createClient(payload) {
  const { data } = await axios.post("/api/clients/", payload, {
    headers: { "Content-Type": "application/json" },
  });
  return data;
}

export async function updateClient(id, payload) {
  const { data } = await axios.put(`/api/clients/${id}/`, payload, {
    headers: { "Content-Type": "application/json" },
  });
  return data;
}

// Desactiva la cuenta (el backend no borra: arrastra reservas y campañas).
// Elimina la cuenta del cliente (y su empresa si queda sin contactos y nada
// la retiene). Las reservas quedan, con el nombre y correo copiados.
export async function deleteClient(id) {
  await axios.delete(`/api/clients/${id}/`);
}

// Sube el logotipo de la compañía a S3 y devuelve { logo: "<url>" }.
export async function uploadCompanyLogo(id, file) {
  const formData = new FormData();
  formData.append("file", file);
  const { data } = await axios.post(`/api/companies/${id}/upload_logo/`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

// --- Productoras (admin manage; managers can list to pick one) --------------

export async function fetchProductoras() {
  const { data } = await axios.get("/api/productoras/");
  return data;
}

export async function fetchProductora(id) {
  const { data } = await axios.get(`/api/productoras/${id}/`);
  return data;
}

export async function createProductora(payload) {
  const { data } = await axios.post("/api/productoras/", payload, {
    headers: { "Content-Type": "application/json" },
  });
  return data;
}

export async function updateProductora(id, payload) {
  const { data } = await axios.put(`/api/productoras/${id}/`, payload, {
    headers: { "Content-Type": "application/json" },
  });
  return data;
}

export async function deleteProductora(id) {
  await axios.delete(`/api/productoras/${id}/`);
}

// --- Campañas de preventa (sólo super admin) --------------------------------

export async function fetchCampaigns() {
  const { data } = await axios.get("/api/campaigns/");
  return data;
}

export async function fetchCampaign(id) {
  const { data } = await axios.get(`/api/campaigns/${id}/`);
  return data;
}

// Listas de opciones de los selectores del asistente (géneros, temporadas, …).
export async function fetchCampaignOptions() {
  const { data } = await axios.get("/api/campaigns/opciones/");
  return data;
}

// Obras y clientes que hacen match con los parámetros elegidos.
export async function matchCampaign(params) {
  const { data } = await axios.post("/api/campaigns/match/", params, {
    headers: { "Content-Type": "application/json" },
  });
  return data;
}

// Asunto y HTML del correo antes de guardar la campaña.
// Ids de TODO lo que cumple el match (filtros + buscadores), sin paginar.
// Lo usa "seleccionar todos" del paso 3.
export async function matchCampaignIds(params) {
  const { data } = await axios.post("/api/campaigns/match/ids/", params, {
    headers: { "Content-Type": "application/json" },
  });
  return data; // { play_ids, plays_count, client_ids, clients_count }
}

export async function previewCampaign(payload) {
  const { data } = await axios.post("/api/campaigns/preview/", payload, {
    headers: { "Content-Type": "application/json" },
  });
  return data; // { html: "<...>", subject: "..." }
}

// Manda el correo de la campaña a una casilla para revisarlo. Usa el mismo
// payload que la previsualización, así funciona sin guardar la campaña.
export async function sendCampaignTest(payload) {
  const { data } = await axios.post("/api/campaigns/prueba/", payload, {
    headers: { "Content-Type": "application/json" },
  });
  return data;
}

export async function previewCampaignById(id) {
  const { data } = await axios.get(`/api/campaigns/${id}/preview/`);
  return data;
}

export async function createCampaign(payload) {
  const { data } = await axios.post("/api/campaigns/", payload, {
    headers: { "Content-Type": "application/json" },
  });
  return data;
}

export async function updateCampaign(id, payload) {
  const { data } = await axios.put(`/api/campaigns/${id}/`, payload, {
    headers: { "Content-Type": "application/json" },
  });
  return data;
}

export async function deleteCampaign(id) {
  await axios.delete(`/api/campaigns/${id}/`);
}

export async function sendCampaign(id) {
  const { data } = await axios.post(`/api/campaigns/${id}/send/`, {});
  return data;
}

// "Guardar para envío": deja la campaña pendiente/programada, sin enviarla.
// Adelanta el envío de una campaña agendada (desde el detalle).
export async function forceSendCampaign(id) {
  const { data } = await axios.post(`/api/campaigns/${id}/forzar-envio/`, {}, {
    headers: { "Content-Type": "application/json" },
  });
  return data;
}

export async function saveCampaignForLater(id) {
  const { data } = await axios.post(`/api/campaigns/${id}/guardar-para-envio/`, {});
  return data;
}

export async function fetchCampaignRecipients(id) {
  const { data } = await axios.get(`/api/campaigns/${id}/destinatarios/`);
  return data;
}

export async function saveCompany(id, payload, isFile = false) {
  let requestBody;
  let headers = {};
  if (isFile) {
    requestBody = new FormData();
    Object.entries(payload).forEach(([key, value]) => {
      if (value === null || value === undefined) return;
      if (Array.isArray(value)) {
        value.forEach((v) => requestBody.append(key, v));
      } else if (value instanceof File) {
        requestBody.append(key, value);
      } else {
        requestBody.append(key, value);
      }
    });
  } else {
    requestBody = payload;
    headers["Content-Type"] = "application/json";
  }
  const { data } = await axios.put(`/api/companies/${id}/`, requestBody, { headers });
  return data;
}

// --- Password recovery -------------------------------------------------------
// Endpoints are Django function views that read request.POST/GET, so send the
// data as x-www-form-urlencoded (URLSearchParams), not JSON.

// Confirmación de correo: el token viene en la URL del enlace del correo.
export async function confirmEmail(token) {
  const body = new URLSearchParams({ token });
  const { data } = await axios.post("/confirmar-correo/confirmar/", body);
  return data;
}

export async function resendVerificationEmail(email) {
  const body = new URLSearchParams({ email });
  const { data } = await axios.post("/confirmar-correo/reenviar/", body);
  return data;
}

export async function requestPasswordReset(email) {
  const body = new URLSearchParams({ email });
  const { data } = await axios.post("/password_reset/", body);
  return data;
}

export async function validatePasswordResetToken(uid, token) {
  const { data } = await axios.get("/password_reset/validate/", {
    params: { uid, token },
  });
  return data;
}

export async function confirmPasswordReset(uid, token, password) {
  const body = new URLSearchParams({ uid, token, password });
  const { data } = await axios.post("/password_reset/confirm/", body);
  return data;
}
